import React from 'react';
import { useLanguage } from '../../utils/languageContext.js';
import { getTranslation } from '../../utils/translate.js';
import Button from '../comman/button.js';

/**
 * AuthErrorBoundary - Error boundary specifically for authentication errors
 * 
 * Best practices:
 * - Catches auth-related errors and provides recovery options
 * - Shows user-friendly error messages
 * - Provides retry and logout options
 * - Prevents app crash on auth failures
 */
class AuthErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { 
            hasError: false, 
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error) {
        // Always render the fallback. Returning null while componentDidCatch
        // calls setState makes React remount the children on every error,
        // which turns any render crash into an infinite remount/refetch loop.
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Log error for debugging
        console.error('AuthErrorBoundary caught an error:', error, errorInfo);
        
        this.setState({
            error,
            errorInfo
        });

        // Optionally log to error reporting service
        // logErrorToService(error, errorInfo);
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
        
        // Clear auth state and redirect to login
        localStorage.removeItem('authToken');
        localStorage.removeItem('fullNameHy');
        window.location.href = '/login';
    };

    handleLogout = () => {
        // Clear all auth data
        localStorage.clear();
        window.location.href = '/login';
    };

    render() {
        if (this.state.hasError) {
            return (
                <AuthErrorFallback 
                    error={this.state.error}
                    onRetry={this.handleRetry}
                    onLogout={this.handleLogout}
                />
            );
        }

        return this.props.children;
    }
}

/**
 * AuthErrorFallback - User-friendly error UI
 */
function AuthErrorFallback({ error, onRetry, onLogout }) {
    const { language } = useLanguage();
    
    const errorMessage = error?.response?.status === 401 || error?.response?.status === 403
        ? getTranslation('errors.unauthorized', language) || 'Your session has expired. Please log in again.'
        : getTranslation('errors.authError', language) || 'An authentication error occurred.';

    return (
        <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#0d1117] px-4">
            <div className="max-w-md w-full bg-white dark:bg-[#161b22] rounded-lg shadow-lg p-6 text-center">
                <div className="mb-4">
                    <svg 
                        className="mx-auto h-12 w-12 text-red-500" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                    >
                        <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                        />
                    </svg>
                </div>
                
                <h2 className="text-xl font-semibold text-gray-900 dark:text-[#c9d1d9] mb-2">
                    {getTranslation('errors.authErrorTitle', language) || 'Authentication Error'}
                </h2>
                
                <p className="text-gray-600 dark:text-[#8b949e] mb-6">
                    {errorMessage}
                </p>
                
                <div className="flex gap-3 justify-center">
                    <Button
                        variant="primary"
                        onClick={onRetry}
                    >
                        {getTranslation('common.retry', language) || 'Retry'}
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={onLogout}
                    >
                        {getTranslation('common.logout', language) || 'Logout'}
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default AuthErrorBoundary;

